import React, { memo, useCallback, useMemo } from "react";
import {
  Box,
  Divider,
  Paper,
  Typography,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  RadioGroup,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { SortableElement, SortableHandle } from "react-sortable-hoc";
import { DragIndicator } from "@material-ui/icons";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import {
  addFormFieldAtIndex,
  removeFormField,
  setEditingField,
} from "redux/actions/FormBuilder";
import CheckItem from "./CheckItem";
import RadioItem from "./RadioItem";

const useStyles = makeStyles((theme) => ({
  divider: {
    marginBlock: "16px",
  },
  title: {
    color: theme.palette.primary.main,
    fontWeight: "bold",
  },
  container: {
    padding: "16px",
  },
  editButton: {
    marginRight: "16px",
  },
  pointer: {
    cursor: "pointer",
  },
  formControl: {
    width: "100%",
  },
}));

const DragHandle = SortableHandle(() => <DragIndicator />);

const FormField = memo((props) => {
  const { item, itemIndex } = props;
  const { editingField, formSettings } = useSelector(
    ({ formBuilder }) => ({
      editingField: formBuilder.editingField,
      formSettings: formBuilder.formSettings,
    }),
    shallowEqual
  );

  const dispatch = useDispatch();
  const classes = useStyles();

  const handleRemoveItem = useCallback(() => {
    dispatch(removeFormField(item.id || item._id));
    dispatch(setEditingField(""));
  }, [dispatch, item.id, item._id]);

  const handleCopy = useCallback(() => {
    const { _id,title, ...rest } = item || {};
    const itemCopy = { ...rest,title:`${title}(کپی)`, id: `${Date.now()}-${Math.random()}` };
    dispatch(addFormFieldAtIndex(itemCopy,itemIndex+1));
  }, [dispatch, item]);

  const fieldByType = useMemo(() => {
    if (
      item.type === "Textfield" ||
      item.type === "Number" ||
      item.type === "Phone"
    ) {
      return (
        <TextField
          label={item.placeHolder}
          variant="outlined"
          className={classes.formControl}
          disabled={true}
        />
      );
    } else if (item.type === "TextArea") {
      return (
        <TextField
          label={item.placeHolder}
          variant="outlined"
          className={classes.formControl}
          disabled={true}
          multiline={true}
          rows={4}
        />
      );
    } else if (item.type === "CheckBox") {
      return item.selectionList.map((checkItem, index) => (
        <CheckItem
          key={index}
          item={checkItem}
          formId={formSettings.formId}
          fieldId={item._id}
        />
      ));
    } else if (item.type === "RadioButton") {
      return (
        <Box marginLeft={"20px"} >
          <RadioGroup aria-label="gender" name="gender1" value={""}>
            {item.selectionList.map((radioItem, index) => (
              <RadioItem
                key={index}
                item={radioItem}
                formId={formSettings.formId}
                fieldId={item._id}
              />
            ))}
          </RadioGroup>
        </Box>
      );
    }
    return null;
  }, [item, formSettings.formId, classes.formControl]);

  return (
    <Box marginY={"16px"}  id={item.id|| item._id } >
      <Paper elevation={2} className={classes.container}>
        <Box display="flex" justifyContent="end" className={classes.pointer}>
          <DragHandle />
        </Box>
        <Box display="flex" marginBottom="16px">
          <Typography className={classes.title}>عنوان :</Typography>
          <Typography color="textSecondary">{item.title}</Typography>
        </Box>
        <Box display="flex" marginBottom="16px">
          <Typography className={classes.title}>زیر عنوان :</Typography>
          <Typography color="textSecondary">{item.subtitle}</Typography>
        </Box>
        <Box marginBottom="16px">{fieldByType}</Box>
        <Box display="flex" marginBottom="16px">
          <Typography className={classes.title}>توضیحات:</Typography>
          <Typography color="textSecondary" style={{ whiteSpace: 'pre-wrap' }}>{item.desc}</Typography>
        </Box>

        {item.type !== "Text" && (
          <>
            <FormControlLabel
              control={
                <Checkbox name="ضروری" checked={item.required} disabled />
              }
              label="ضروری"
            />
            <FormControlLabel
              control={
                <Checkbox name="سوال آزمون" checked={item.examQuestion} disabled />
              }
              label="سوال آزمون"
            />
              <FormControlLabel
              control={
                <Checkbox name="نمایش شرطی" checked={item.visibilityCondition} disabled />
              }
              label="نمایش شرطی"
            />
          </>
        )}

        <Divider className={classes.divider} />
        <Box display="flex" justifyContent="end">
          {editingField !== (item.id || item._id) && (
            <Button
              variant="contained"
              color="primary"
              className={classes.editButton}
              onClick={() => {
                dispatch(setEditingField(item.id || item._id));
                window.scrollTo({
                  top: 0,
                  behavior: "smooth",
                });
              }}
            >
              ویرایش
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            onClick={handleCopy}
            className={classes.editButton}
          >
            کپی
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleRemoveItem}
          >
            حذف
          </Button>
        </Box>
      </Paper>
    </Box>
  );
});

export default SortableElement(FormField);
